import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Bootstrap, Game, Settings } from '../shared/types';
import { api, json } from './api';
interface Store {
  data: Bootstrap | null;
  error: string;
  refresh: () => Promise<void>;
  notify: (message: string) => void;
  toggleFavorite: (game: Game) => Promise<void>;
  toast: string;
}
const Context = createContext<Store>(null!);
export function Provider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<Bootstrap | null>(null),
    [error, setError] = useState(''),
    [toast, setToast] = useState('');
  const refresh = useCallback(async () => {
    try {
      setData(await api<Bootstrap>('/bootstrap'));
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    let active = true;
    let pending = false;
    const syncSettings = async () => {
      if (document.hidden || pending) return;
      pending = true;
      try {
        const settings = await api<Settings>('/settings');
        if (active)
          setData((current) =>
            current && JSON.stringify(current.settings) !== JSON.stringify(settings)
              ? { ...current, settings }
              : current,
          );
      } catch {
        // Existing pages remain usable while offline; the server still enforces access.
      } finally {
        pending = false;
      }
    };
    const timer = setInterval(() => void syncSettings(), 30000);
    window.addEventListener('focus', syncSettings);
    document.addEventListener('visibilitychange', syncSettings);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('focus', syncSettings);
      document.removeEventListener('visibilitychange', syncSettings);
    };
  }, []);
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(''), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);
  async function toggleFavorite(game: Game) {
    if (!data?.user) {
      setToast('登录后即可收藏游戏');
      return;
    }
    try {
      const favorite = !data.favorites.includes(game.id);
      await api('/favorites/' + game.id, json('PUT', { favorite }));
      await refresh();
      setToast(favorite ? '已加入收藏' : '已取消收藏');
    } catch (e) {
      setToast((e as Error).message);
    }
  }
  return (
    <Context.Provider value={{ data, error, refresh, notify: setToast, toggleFavorite, toast }}>
      {children}
      <div className={`toast ${toast ? 'visible' : ''}`} role="status">
        {toast}
      </div>
    </Context.Provider>
  );
}
export const usePlatform = () => useContext(Context);
